import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import I18n from "i18n-js";
import pathToRegexp from "path-to-regexp";
import { array, bool, func, shape, string } from "prop-types";

import { getNomenclatures } from "../../../../nomenclatures/actions/nomenclaturesActions";
import { getClientsList } from "../../../../company/actions/clientsActions";
import { getSitesList } from "../../../../company/actions/sitesActions";

import useInstallationEditFormState from "../hooks/useInstallationEditFormState";

import FormLayout from "../../../../common/components/layout/FormLayout";
import Select from "../../../../common/components/form/Select";
import StatelessTextInput from "../../../../common/components/form/StatelessTextInput";
import DateInput from "../../../../common/components/form/DateInput";
import StatelessCheckbox from "../../../../common/components/form/StatelessCheckbox";
import SubmitButton from "../../../../common/components/form/SubmitButton";
import { StandaloneInvalidFeedback } from "../../../../common/components/form/InvalidFeedback";

import InstallationEditFormClientSection from "./InstallationEditFormClientSection";
import InstallationEditFormSiteSection from "./InstallationEditFormSiteSection";

import { ROUTE_INSTALLATION, ROUTE_INSTALLATIONS } from "../../../../../routes";
import { RESET_INSTALLATION_STATE } from "../../../constants";
import InstallationEditFormTablesSection from "./InstallationEditFormTablesSection";
import NoData from "../../../../common/components/elements/NoData";

import {
  bigLabelFormInputProps,
  SET_CURRENT_LOAD,
  SET_DESIGNATION,
  SET_EXTERNAL_ID,
  SET_FLUID_CODE,
  SET_INTEGRATED_LEAK_DETECTOR,
  SET_LAST_LEAK_DETECTION_DATE,
  SET_MARK,
  SET_NOMINAL_LOAD,
  smallLabelFormInputProps,
} from "../constants";

function formatDateToString(date) {
  if (!date) return "";
  const mouth = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${day}/${mouth}/${date.getFullYear()}`;
}
function InstallationEditForm({
  installations,
  info,
  clients,
  clientsList,
  getClientsList,
  sites,
  sitesList,
  getSitesList,
  nomenclatures,
  nomenclaturesList,
  getNomenclatures,
  onSubmit: handleSubmit,
  saved,
  history,
  match,
}) {
  const reduxDispatch = useDispatch();
  const [state, dispatch] = useInstallationEditFormState(
    sitesList,
    getSitesList,
    clientsList,
    getClientsList,
    nomenclaturesList,
    getNomenclatures,
    info
  );
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (saved) {
      history.push(
        pathToRegexp.compile(ROUTE_INSTALLATION)({ uuid: match.params.uuid })
      );
      reduxDispatch({ type: RESET_INSTALLATION_STATE });
    }
  }, [saved, history, match.params.uuid, reduxDispatch]);

  useEffect(() => {
    if (installations.errorMessage) {
      setErrors((prev) => {
        return { ...prev, global: installations.errorMessage };
      });
    }
  }, [installations.errorMessage, setErrors]);

  useEffect(() => {
    if (installations.errorCode === 400) {
      setErrors((prev) => {
        return {
          ...prev,
          designation: I18n.t(
            "pages.installations.actions.edit.error.designation_not_unique"
          ),
          mark: I18n.t(
            "pages.installations.actions.edit.error.mark_not_unique"
          ),
        };
      });
    }
  }, [installations.errorCode, setErrors]);

  if (
    installations.inProgress ||
    nomenclatures.inProgress ||
    nomenclaturesList === undefined ||
    clients.inProgress ||
    sites.inProgress
  ) {
    return <div className="loader" />;
  }

  if (
    installations.error ||
    nomenclatures.error ||
    clients.error ||
    sites.error
  ) {
    return <p>{I18n.t("common.loadingApiError")}</p>;
  }

  const isFormValid = () => {
    const fields = [
      "client",
      "site",
      "designation",
      "mark",
      "integratedLeakDetector",
      "fluidCode",
      "currentLoad",
      "nominalLoad",
    ];

    let errors = {};
    let isValid = true;

    fields.forEach((field) => {
      if (state[field] == null || state[field] === "") {
        errors[field] = "This field is required";
        isValid = false;
      }
    });
    if (isValid == false)
      errors["global"] = "Some fields are not filled correctly";
    setErrors(errors);

    return isValid;
  };

  const onSubmit = () => {
    if (!isFormValid()) {
      return;
    }

    const installation = {
      clientUuid: state.client,
      siteUuid: state.site,
      externalId: state.externalId,
      designation: state.designation,
      mark: state.mark,
      lastLeakDetectionDate: state.lastLeakDetectionDate?.toISOString(),
      integratedLeakDetector: state.integratedLeakDetector,
      fluidCode: state.fluidCode,
      currentLoad: state.currentLoad,
      nominalLoad: state.nominalLoad,
      components: state.addedComponents,
    };

    handleSubmit(installation);
  };

  return (
    <FormLayout
      title={I18n.t("pages.installations.view.title")}
      backRoute={ROUTE_INSTALLATIONS}
      size={FormLayout.LARGE}
    >
      <form>
        <div style={{ display: "flex" }}>
          <div className="card card-block card-half border-end-none border-bottom-none">
            <InstallationEditFormClientSection
              state={state}
              dispatch={dispatch}
              errors={errors}
            />
            <InstallationEditFormSiteSection
              state={state}
              dispatch={dispatch}
              errors={errors}
            />
            <div className="detail-header">
              <div>
                {I18n.t("pages.installations.view.identification:header")}
              </div>
              <div className="detail-header-band"></div>
            </div>
            <StatelessTextInput
              id={"installationId"}
              value={state.externalId ?? ""}
              onChangedValue={(value) =>
                dispatch({ type: SET_EXTERNAL_ID, payload: value })
              }
              label={I18n.t("pages.installations.view.id:label")}
              errors={errors.externalId}
              optional
              {...smallLabelFormInputProps}
            />
            <StatelessTextInput
              id={"installationDesignation"}
              value={state.designation ?? ""}
              onChangedValue={(value) =>
                dispatch({ type: SET_DESIGNATION, payload: value })
              }
              label={I18n.t("pages.installations.view.designation:label")}
              errors={errors.designation}
              {...smallLabelFormInputProps}
            />
            <StatelessTextInput
              id={"installationMark"}
              value={state.mark ?? ""}
              onChangedValue={(value) =>
                dispatch({ type: SET_MARK, payload: value })
              }
              label={I18n.t("pages.installations.view.mark:label")}
              errors={errors.mark}
              {...smallLabelFormInputProps}
            />
            <dl className="row align-center">
              <dt className="col-sm-3">
                {I18n.t("pages.installations.view.barcode:label")}
              </dt>
              <dd className="col-sm-9">{info.barcode ?? <NoData />}</dd>
            </dl>
          </div>
          <div className="card card-block card-half border-bottom-none">
            <div className="detail-header">
              <div>
                {I18n.t("pages.installations.view.technical-data:header")}
              </div>
              <div className="detail-header-band"></div>
            </div>
            <DateInput
              id="date"
              value={formatDateToString(state.lastLeakDetectionDate)}
              label={I18n.t("pages.installations.view.last-control-date:label")}
              onChange={(newDate) =>
                dispatch({
                  type: SET_LAST_LEAK_DETECTION_DATE,
                  payload: newDate,
                })
              }
              error={errors.lastLeakDetectionDate}
              {...bigLabelFormInputProps}
            />
            <div style={{ marginBottom: "1em" }}>
              <StatelessCheckbox
                key="integratedLeakDetector"
                id="integratedLeakDetector"
                label={I18n.t(
                  "pages.installations.view.integrated-leak-detection:label"
                )}
                checked={Boolean(state.integratedLeakDetector)}
                onChangedValue={(checked) =>
                  dispatch({
                    type: SET_INTEGRATED_LEAK_DETECTOR,
                    payload: checked,
                  })
                }
                errors={errors.integratedLeakDetector}
                labelFirst={true}
              />
            </div>
            <Select
              id="fluidCode"
              onChange={(event) =>
                dispatch({ type: SET_FLUID_CODE, payload: event.target.value })
              }
              label={I18n.t("pages.installations.view.cooling-fluid:label")}
              renderOption={(option) => (
                <option key={option.uuid} value={option.uuid}>
                  {option.designation}
                </option>
              )}
              defaultValue={state.fluidCode ?? ""}
              options={nomenclaturesList?.fluids ?? []}
              placeholder={I18n.t(
                "pages.installations.view.cooling-fluid:label"
              )}
              error={errors.fluidCode}
              {...bigLabelFormInputProps}
            />
            <StatelessTextInput
              id={"currentLoad"}
              value={state.currentLoad ?? ""}
              type="number"
              onChangedValue={(value) =>
                dispatch({ type: SET_CURRENT_LOAD, payload: value })
              }
              label={I18n.t("pages.installations.view.current-load:label")}
              errors={errors.currentLoad}
              {...bigLabelFormInputProps}
            />
            <StatelessTextInput
              id={"nominalLoad"}
              value={state.nominalLoad ?? ""}
              type="number"
              onChangedValue={(value) =>
                dispatch({ type: SET_NOMINAL_LOAD, payload: value })
              }
              label={I18n.t("pages.installations.view.nominal-load:label")}
              errors={errors.nominalLoad}
              {...bigLabelFormInputProps}
            />
          </div>
        </div>
        <div className="card card-block mb-3">
          <div className="detail-header">
            <div>{I18n.t("pages.installations.view.components:header")}</div>
            <div className="detail-header-band"></div>
          </div>
          {state.nomenclaturesList && state.nomenclaturesList.length !== 0 ? (
            <InstallationEditFormTablesSection
              installation={info}
              state={state}
              dispatch={dispatch}
              interventions={info.interventions}
              analyses={info.analyses}
            />
          ) : null}
        </div>
        <StandaloneInvalidFeedback
          errors={errors.global}
          classes={"invalid-feedback-right"}
        />
        <SubmitButton loading={false} onClick={onSubmit} />
      </form>
    </FormLayout>
  );
}

const mapStateToProps = (state) => ({
  installations: state.installations,
  info: state.installations.info,

  nomenclatures: state.nomenclatures,
  nomenclaturesList: state.nomenclatures.nomenclatures,

  clients: state.clients,
  clientsList: state.clients.list,

  sites: state.sites,
  sitesList: state.sites.list,
});

const mapDispatchToProps = (dispatch) => ({
  getNomenclatures: () => dispatch(getNomenclatures()),
  getClientsList: () => dispatch(getClientsList()),
  getSitesList: () => dispatch(getSitesList()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InstallationEditForm);

InstallationEditForm.propTypes = {
  installations: shape(),
  info: shape(),
  clients: shape(),
  clientsList: array,
  getClientsList: func.isRequired,
  sites: shape(),
  sitesList: array,
  getSitesList: func.isRequired,
  nomenclatures: shape(),
  nomenclaturesList: shape(),
  getNomenclatures: func.isRequired,
  onSubmit: func.isRequired,
  saved: bool.isRequired,
  history: shape().isRequired,
  match: shape({
    params: shape({
      uuid: string.isRequired,
    }).isRequired,
  }).isRequired,
};
